import React, { useRef } from 'react';
import { AnrufZeile, CDN_BASE_URL } from '../shared';
import { trackMeta } from '../../lib/metaTrack';
import HeroLeadForm from '../HeroLeadForm';

// Two-column hero: founder video placeholder on the left, the three pillars +
// CTA on the right. Set to `false` to ship the single-column (CTA-only) variant.
const VIDEO_ENABLED = true;

// VSL served from DigitalOcean Spaces via the CDN endpoint (same CDN as the
// team portraits; edge-cached, whereas the raw origin sends no Cache-Control at
// all). Object must be public-read. Override the base via REACT_APP_CDN_BASE_URL.
//
// 720p (not 1080p) on purpose: the hero renders this in a ~750px-wide column, so
// 1080p was ~3x more data than the screen can show — which stalled playback on
// weaker mobile connections. 14.7MB / ~0.7 Mbps instead of 40MB / ~2.0 Mbps.
//
// Back on the V1 export: the V3 720p file (2026_VSL_V3_720p.mp4, 24MB) had
// botched video and audio. It also carried HLG/HDR colour tags from FCP, which
// made its look device-dependent — fine on HDR/Safari, flat on SDR/Chrome and
// in-app browsers. Both files are still on the CDN, so this is a one-line swap.
const VSL_SRC = `${CDN_BASE_URL}/2026_VSL_V1_720p.mp4`;

const PILLARS = [
  {
    eyebrow: 'Komplette Übernahme der Verwaltung',
    body: 'Sie sehen und hören nur noch, was Sie wollen. Unser Team von Experten ist ab Tag 1 für Ihre Immobilien da.'
  },
  {
    eyebrow: 'Alle Vorteile ohne zeitlichen Aufwand',
    body: 'Wir geben Ihnen das wertvollste zurück was Sie haben: Ihre Zeit.'
  },
  {
    eyebrow: 'Hundertprozentige Klarheit über die Finanzen',
    body: 'Voller Überblick über die finanzielle Situation Ihrer Immobilie. Sie haben in Ihrem Eigentümerbereich jederzeit vollen Überblick.'
  },
];

/**
 * LandingHero — page hero with headline, optional founder video and three pillars.
 * Pass `onCTA` to wire the primary button. Not auto-numbered (no .section-label.numbered).
 */
export default function LandingHero({ onCTA }) {
  // Meta: ViewContent now means "viewed our VSL" (not a bare pageview). Fires
  // once, when the visitor starts the video. No-op until marketing consent.
  const vslViewed = useRef(false);
  const onVslPlay = () => {
    if (vslViewed.current) return;
    vslViewed.current = true;
    trackMeta('ViewContent', { customData: { content_name: 'Erstanalyse VSL' } });
  };

  const pillars = (
    <div
      style={{
        borderLeft: '2px solid var(--bordeaux)',
        paddingLeft: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      {PILLARS.map((b, i) => (
        <div key={i}>
          <div className="eyebrow" style={{ marginBottom: 4, color: 'var(--anthracite)' }}>
            {b.eyebrow}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--charcoal)' }}>
            {b.body}
          </div>
        </div>
      ))}
    </div>
  );

  // Layout lives in `.hero-grid__cta` / `.btn-primary--filled` (homev2.css) so
  // the CTA can be re-ordered under the video on mobile. Without the video the
  // pillars have no column, so the CTA is centered on its own instead.
  const ctaBlock = (
    <button className="btn-primary btn-primary--filled" onClick={onCTA}>
      Unverbindlich Erstanalyse anfragen
    </button>
  );

  return (
    <section style={{ paddingTop: 5, paddingBottom: 120 }}>
      <div className="shell-wide">
        <div className="reveal" style={{ textAlign: 'center', margin: '0 auto 52px' }}>
          <div className="section-label section-label--center" style={{ marginBottom: 15 }}>
            <span>Für Investoren, Erben und Treuhänder</span>
          </div>
          <h1
            className="h-display"
            style={{
              fontSize: 'clamp(40px, 4.4vw, 60px)',
              maxWidth: 'none',
              margin: '0 auto',
              letterSpacing: '-0.005em',
            }}
          >
          Wir übernehmen die komplette Verwaltung Ihrer Immobilien für Sie.
          </h1>
          <p
            className="lede"
            style={{
              fontSize: 19,
              fontStyle: 'italic',
              fontFamily: 'var(--serif)',
              color: 'var(--charcoal)',
              maxWidth: '90ch',
              margin: '28px auto 0',
            }}
          >
            Stellen Sie es sich vor: Ihre Immobilien laufen plötzlich wie von selbst - und bringen dabei jedes Jahr mehr ein.
          </p>
        </div>

        {VIDEO_ENABLED ? (
          <div className="hero-grid">
            <div className="reveal hero-grid__video">
              {/* #t=0.1 makes the browser paint the frame at 0.1s as a poster
                  instead of a black box before the user hits play (Spaces
                  supports range requests, so only that frame is fetched). */}
              <video
                src={`${VSL_SRC}#t=0.1`}
                controls
                playsInline
                preload="metadata"
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onPlay={onVslPlay}
                style={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  display: 'block',
                  objectFit: 'cover',
                  background: '#0F1116',
                  border: '1px solid #2A2D34',
                }}
              >
                Ihr Browser kann dieses Video nicht abspielen.
              </video>
            </div>
            <div className="reveal hero-grid__pillars">{pillars}</div>
            <div className="reveal hero-grid__cta">{ctaBlock}</div>
          </div>
        ) : (
          // Without the VSL the pillars don't have a column to live in — show
          // just the CTA + tagline, centered under the headline.
          <div className="reveal" style={{ textAlign: 'center' }}>{ctaBlock}</div>
        )}

        {/* Anrufzeile vor dem Formular — derselbe Baustein wie auf der Startseite. */}
        <AnrufZeile />

        {/* Short lead-capture form, directly under the video + CTA. */}
        <HeroLeadForm />
      </div>
    </section>
  );
}
